"use client";

import { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Database, History, RefreshCw } from "lucide-react";

export const DOC_THUMB_SRC = {
  /** สลิปจาก LINE/ระบบ — no photo */
  deposit: "/img/deposit.svg",
  /** ยอดค้างจาก LINE/ระบบ — no photo */
  receipt: "/img/receipt.svg",
  /** ใบเสร็จจาก LINE/ระบบ — no photo */
  withdraw: "/img/withdraw.svg",
  /** ใบแจ้งหนี้จาก LINE/ระบบ — no photo */
  invoice: "/img/invoice.svg",
} as const;

export type DocThumbGlyph = keyof typeof DOC_THUMB_SRC | "bank" | "history";

export function FinancialWorkDocThumb(props: {
  glyph: DocThumbGlyph;
  incoming: boolean;
  review?: boolean;
  /** Evidence slip: show document art only — no queue border or direction badge. */
  plain?: boolean;
  /** Same-origin slip/income image — falls back to glyph art when missing or broken. */
  imageUrl?: string;
  onClick?: () => void;
  testId?: string;
}) {
  const { glyph, incoming, review = false, plain = false } = props;
  const [imageFailed, setImageFailed] = useState(false);
  const photoUrl = props.imageUrl?.trim() || "";
  const showPhoto = Boolean(photoUrl) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl]);

  const Arrow = incoming ? ArrowUpRight : ArrowDownRight;
  const isIconGlyph = glyph === "history" || glyph === "bank";
  const thumbSize = "h-[52px] w-[52px]";
  const thumbRound = "rounded-[10px]";
  const iconSize = isIconGlyph ? "h-6 w-6" : "h-[70%] w-[70%]";
  const badgeSize = "h-4 w-4";
  const badgeIconSize = "h-2.5 w-2.5";
  const frame = (
    <span
      className={[
        "relative flex items-center justify-center overflow-hidden",
        thumbSize,
        thumbRound,
        showPhoto || isIconGlyph
          ? "bg-aera-offwhite"
          : "bg-aera-offwhite px-2.5 pt-1.5",
        plain
          ? ""
          : [
              glyph === "history" || review ? "border" : "border border-dashed",
              incoming ? "border-aera-green" : "border-aera-red",
            ].join(" "),
      ].join(" ")}
    >
      <span
        className={
          showPhoto || !isIconGlyph
            ? "relative h-full w-full overflow-hidden rounded-sm bg-aera-white"
            : "relative flex h-full w-full items-center justify-center"
        }
      >
        {showPhoto ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={photoUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        ) : glyph === "bank" ? (
          <Database
            className={`${iconSize} text-aera-slate`}
            strokeWidth={2}
            aria-hidden
          />
        ) : glyph === "history" ? (
          <History
            className={`${iconSize} ${incoming ? "text-aera-green" : "text-aera-red"}`}
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={DOC_THUMB_SRC[glyph]}
            alt=""
            width={40}
            height={40}
            className="absolute left-1/2 top-1/2 h-[200%] w-[200%] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
          />
        )}
      </span>
    </span>
  );

  return (
    <span
      className={`relative shrink-0 overflow-visible ${thumbSize}`}
      data-testid={props.testId ?? "fw-doc-thumb"}
      data-thumb-glyph={glyph}
      data-thumb-dir={incoming ? "in" : "out"}
      data-thumb-has-image={showPhoto ? "true" : "false"}
    >
      {props.onClick ? (
        <button
          type="button"
          className={`block cursor-pointer ${thumbSize}`}
          onClick={props.onClick}
        >
          {frame}
        </button>
      ) : (
        frame
      )}
      {review && !plain ? (
        <span
          className={`pointer-events-none absolute -right-[3px] -top-[3px] flex items-center justify-center rounded-full border border-aera-lightgray bg-aera-white text-aera-gray ${badgeSize}`}
          aria-hidden
        >
          <RefreshCw className={badgeIconSize} strokeWidth={2.5} />
        </span>
      ) : null}
      {!plain ? (
        <span
          className={`pointer-events-none absolute -bottom-[3px] -right-[3px] flex items-center justify-center rounded-[4px] text-aera-white ${badgeSize} ${
            incoming ? "bg-aera-green" : "bg-aera-red"
          }`}
          aria-hidden
        >
          <Arrow className={badgeIconSize} strokeWidth={3} />
        </span>
      ) : null}
    </span>
  );
}
